import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';

export default function About() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="ShopManager247 logo" className="h-10 w-10 rounded-lg" />
            <span className="text-xl font-bold text-white tracking-tight">ShopManager<span className="text-orange-400">247</span></span>
          </a>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')} className="text-slate-300 hover:text-white hover:bg-white/10" data-testid="about-login-btn">
              Log In
            </Button>
            <Button onClick={() => navigate('/pricing')} className="bg-orange-500 hover:bg-orange-600 text-white font-semibold" data-testid="about-signup-btn">
              Start Free Trial
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-20" data-testid="about-page">
        <h1 className="text-4xl sm:text-5xl font-black text-white mb-6">About ShopManager247</h1>
        <p className="text-lg text-slate-400 leading-relaxed mb-6">
          ShopManager247 is job management software for industrial and mechanical repair shops — pump and
          compressor overhauls, motor rewinds, gearbox rebuilds and the field work that goes with them. It was
          built inside a working rotating-equipment shop, from the whiteboards, spreadsheets and email threads
          that never quite matched what was happening on the floor.
        </p>

        <h2 className="text-lg md:text-lg font-bold text-white mt-12 mb-3">What it does</h2>
        <p className="text-slate-400 leading-relaxed mb-4">
          One place to schedule jobs on a Gantt timeline, move them through your own workflow states, dispatch
          field crews, log technician hours and overtime, track every piece of equipment by serial number,
          capture readings on fillable PDF forms, and see the margin on a job before the invoice leaves the
          office. Schedules import from and export to Microsoft Project, so planners and customers keep working
          in the tool they already use.
        </p>

        <h2 className="text-lg md:text-lg font-bold text-white mt-12 mb-3">Who it is for</h2>
        <p className="text-slate-400 leading-relaxed mb-4">
          Shops running anywhere from a handful of technicians to multiple divisions. Roles cover management,
          foremen, shop technicians, field techs, field coordinators and admin/safety staff, each with their own
          permissions, so the schedule stays trustworthy and wage data stays private.
        </p>

        <h2 className="text-lg md:text-lg font-bold text-white mt-12 mb-3">How pricing works</h2>
        <p className="text-slate-400 leading-relaxed mb-8">
          A small annual platform fee per shop plus a low monthly per-user charge, with a one-month free trial on
          every plan and no credit card required to start. Current prices are always listed on the pricing page.
        </p>

        <Button onClick={() => navigate('/pricing')} className="bg-orange-500 hover:bg-orange-600 text-white font-semibold h-12 px-8" data-testid="about-pricing-cta">
          See Pricing & Start Free Trial <ArrowRight size={18} className="ml-2" />
        </Button>
      </main>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4">
          <span className="text-sm text-slate-500">ShopManager247</span>
          <nav className="flex items-center gap-5 text-sm text-slate-500">
            <a href="/features" className="hover:text-slate-300">Features</a>
            <a href="/pricing" className="hover:text-slate-300">Pricing</a>
            <a href="/about" className="hover:text-slate-300">About</a>
            <a href="/login" className="hover:text-slate-300">Log In</a>
          </nav>
          <p className="text-sm text-slate-600">&copy; {new Date().getFullYear()} ShopManager247. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
